/**
 * Admissions API client, shared by the public portal and the staff workspace.
 *
 * `admissionRequest` takes paths relative to /api, because callers mix public
 * admission routes ("/admission-rounds", "/admissions/applications") with admin
 * ones ("/admin/applications/..."). Families authenticate with a resume token
 * rather than an account; staff use the admin bearer token.
 */
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

/** Admin JWT saved by the auth provider; null for public callers. */
function adminToken() {
  try {
    return JSON.parse(localStorage.getItem("kcgs_admin_session"))?.token || null;
  } catch {
    return null;
  }
}

/**
 * One request.
 *   admissionRequest("/admission-rounds")                       → GET
 *   admissionRequest(path, { token })                           → GET with a resume token
 *   admissionRequest(path, { method: "PATCH", body: {...} })    → JSON
 *   admissionRequest(path, { method: "POST",  body: formData }) → multipart
 *
 * A FormData body must not carry an explicit Content-Type: the browser has to
 * set it so the multipart boundary is included.
 */
export async function admissionRequest(path, { method = "GET", body, token, admin, signal } = {}) {
  const headers = {};
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;

  if (admin) {
    const t = adminToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  }

  // A resume token travels in the X-Resume-Token header, never the query
  // string: query strings end up in server logs and browser history, and this
  // token is the only thing standing between a stranger and a family's
  // documents. Writes also repeat it in the body, which is what they validate.
  if (token) headers["X-Resume-Token"] = token;

  let payload = body;
  if (body && !isForm) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, { method, headers, body: payload, signal });

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(data?.error?.message || "Something went wrong. Please try again.");
    err.code = data?.error?.code;
    err.status = res.status;
    throw err;
  }
  return data;
}

/**
 * Stream a private document to the browser as a download.
 *
 * These files are never publicly served, so the request has to carry proof —
 * the family's resume token, or the staff bearer token — and the bytes are
 * turned into a blob rather than linked to directly.
 */
export async function downloadAdmissionFile(path, filename, token, admin) {
  const headers = {};
  if (admin) {
    const t = adminToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  }

  if (token) headers["X-Resume-Token"] = token;

  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error?.message || "Could not download that file.");
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick; revoking immediately can cancel the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ------------------------------------------------------- draft access -----
 * The resume token is the only way back into an unfinished application, so it
 * is kept on the device as well as shown to the family.
 */
const STORE_KEY = "kcgs_admission_draft";

export function saveDraftAccess({ applicationNo, resumeToken }) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ applicationNo, resumeToken }));
  } catch {
    /* private browsing — the family still has the code on screen */
  }
}

export function loadDraftAccess() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "null");
  } catch {
    return null;
  }
}

export function clearDraftAccess() {
  try {
    localStorage.removeItem(STORE_KEY);
  } catch {
    /* nothing to do */
  }
}

/* ------------------------------------------------- portal convenience ----- */

/**
 * Active admission rounds, as a stable fetcher for useResource — which needs a
 * named export it can use as an effect dependency, and passes an AbortSignal.
 */
export const getLiveAdmissionRounds = ({ signal } = {}) =>
  admissionRequest("/admission-rounds", { signal });


/** Begin an application for a class the round actually offers. */
export const startApplication = ({ institution, classApplied, groupApplied, startToken }) =>
  admissionRequest("/admissions/applications", {
    method: "POST",
    body: { institution, classApplied, groupApplied: groupApplied || null, startToken },
  });

/** Reopen a draft with its number + token. */
export const loadApplication = (applicationNo, resumeToken) =>
  admissionRequest(`/admissions/applications/${encodeURIComponent(applicationNo)}`, {
    token: resumeToken,
  });

/** Save progress. Answers are merged server-side, so partial sends are fine. */
export const saveApplication = (applicationNo, resumeToken, answers, extra = {}) =>
  admissionRequest(`/admissions/applications/${encodeURIComponent(applicationNo)}`, {
    method: "PATCH",
    body: { resumeToken, answers, ...extra },
  });

/** Submit. Idempotent: a retry returns the existing application unchanged. */
export const submitApplicationForm = (applicationNo, resumeToken) =>
  admissionRequest(`/admissions/applications/${encodeURIComponent(applicationNo)}/submit`, {
    method: "POST",
    body: { resumeToken },
  });
